import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, Loader2, Gift } from 'lucide-react';
import { ConsentCheckboxes } from '@/components/forms/ConsentCheckboxes';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { pmsFrom, invokeFunction } from '@/integrations/pms';
import { useSiteContent } from '@/context/SiteContentContext';
import { leadFormSchema, type LeadFormData } from '@/lib/validations/formSchemas';
import { cn } from '@/lib/utils';
import { linkSessionToLead } from '@/hooks/usePageTracking';

interface LeadFormProps {
  className?: string;
  title?: string;
  subtitle?: string;
  showPromoField?: boolean;
  defaultService?: string;
  source?: string;
  onSuccess?: () => void;
}

export const LeadForm: React.FC<LeadFormProps> = ({
  className,
  title = 'Get Your Free Quote',
  subtitle = 'Fill out the form and we\'ll get back to you within 24 hours.',
  showPromoField = true,
  defaultService,
  source = 'lead_form',
  onSuccess,
}) => {
  const { services } = useSiteContent();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    setValue,
    watch,
  } = useForm<LeadFormData>({
    resolver: zodResolver(leadFormSchema),
    defaultValues: {
      service: defaultService || '',
      consentEmail: false,
      consentSms: false,
      acceptTerms: false,
    },
  });

  const onSubmit = async (data: LeadFormData) => {
    setServerError(null);
    
    try {
      const { data: insertedLead, error } = await pmsFrom('leads').insert({
        name: data.name,
        email: data.email,
        phone: data.phone,
        message: data.message || null,
        service_needed: data.service,
        customer_type: data.customerType || 'new',
        promo_code: data.promoCode || null,
        source,
      }).select('id').single();

      if (error) throw error;

      // Link the current session to the new lead for tracking
      if (insertedLead?.id) {
        await linkSessionToLead(insertedLead.id);
        
        // Send lead notification email (fire and forget)
        invokeFunction('notify-lead', {
          body: {
            lead_id: insertedLead.id,
            name: data.name,
            email: data.email,
            phone: data.phone,
            service_needed: data.service,
            message: data.message,
            promo_code: data.promoCode,
            source,
          },
        }).catch(err => console.error('Failed to send lead notification:', err));
      }

      setIsSubmitted(true);
      reset();
      onSuccess?.();

      setTimeout(() => setIsSubmitted(false), 5000);
    } catch (err) {
      console.error('Lead form error:', err);
      setServerError('Something went wrong. Please try again or call us directly.');
    }
  };

  if (isSubmitted) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className={cn('text-center py-12', className)}
      >
        <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="h-8 w-8 text-green-600 dark:text-green-400" />
        </div>
        <h2 className="font-heading text-xl font-semibold mb-2">Thank You!</h2>
        <p className="text-muted-foreground">We'll contact you within 24 hours with your free quote.</p>
      </motion.div>
    );
  }

  return (
    <div className={className}>
      {(title || subtitle) && (
        <div className="text-center mb-6">
          {title && <h2 className="font-heading text-xl font-semibold mb-2">{title}</h2>}
          {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" data-form-name="Contact Form">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="lead-name">Name *</Label>
            <Input
              id="lead-name"
              placeholder="Your name"
              {...register('name')}
              aria-invalid={!!errors.name}
              className={errors.name ? 'border-destructive' : ''}
            />
            {errors.name && (
              <p className="text-xs text-destructive">{errors.name.message}</p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="lead-phone">Phone *</Label>
            <Input
              id="lead-phone"
              type="tel"
              placeholder="(555) 123-4567"
              {...register('phone')}
              aria-invalid={!!errors.phone}
              className={errors.phone ? 'border-destructive' : ''}
            />
            {errors.phone && (
              <p className="text-xs text-destructive">{errors.phone.message}</p>
            )}
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="lead-email">Email *</Label>
          <Input
            id="lead-email"
            type="email"
            placeholder="you@example.com"
            {...register('email')}
            aria-invalid={!!errors.email}
            className={errors.email ? 'border-destructive' : ''}
          />
          {errors.email && (
            <p className="text-xs text-destructive">{errors.email.message}</p>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="lead-service">Service Needed *</Label>
            <select
              id="lead-service"
              {...register('service')}
              className={cn(
                'w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                errors.service ? 'border-destructive' : ''
              )}
            >
              <option value="">Select a service...</option>
              {services.map((service) => (
                <option key={service.slug} value={service.title}>
                  {service.title}
                </option>
              ))}
            </select>
            {errors.service && (
              <p className="text-xs text-destructive">{errors.service.message}</p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="lead-customer-type">Customer Type *</Label>
            <select
              id="lead-customer-type"
              {...register('customerType')}
              className={cn(
                'w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                errors.customerType ? 'border-destructive' : ''
              )}
            >
              <option value="new">New Customer</option>
              <option value="returning">Returning Customer</option>
              <option value="current">Current Customer</option>
            </select>
            {errors.customerType && (
              <p className="text-xs text-destructive">{errors.customerType.message}</p>
            )}
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="lead-message">Additional Details</Label>
          <Textarea
            id="lead-message"
            placeholder="Tell us about your project or issue..."
            rows={3}
            {...register('message')}
            className="resize-none"
          />
        </div>

        {showPromoField && (
          <div className="space-y-1.5">
            <Label htmlFor="lead-promo" className="flex items-center gap-2">
              <Gift className="h-4 w-4 text-primary" />
              Promo Code
            </Label>
            <Input
              id="lead-promo"
              placeholder="Enter promo code (optional)"
              {...register('promoCode')}
            />
          </div>
        )}

        <AnimatePresence>
          {serverError && (
            <motion.p
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="text-sm text-destructive bg-destructive/10 p-3 rounded-md"
            >
              {serverError}
            </motion.p>
          )}
        </AnimatePresence>

        <ConsentCheckboxes register={register} setValue={setValue} watch={watch} errors={errors} />

        <Button type="submit" size="lg" className="w-full" disabled={isSubmitting}>
          {isSubmitting ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Submitting...
            </>
          ) : (
            'Get Free Estimate'
          )}
        </Button>
      </form>
    </div>
  );
};
